import React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Logo from '../../components/Logo';
import { 
  ShoppingCart, 
  DollarSign, 
  Package, 
  Store,
  TrendingUp,
  Clock,
  Users,
  Power
} from 'lucide-react';
import toast from 'react-hot-toast';
import './Dashboard.css';

const Dashboard = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Buscar estabelecimento primeiro
  const { data: estabelecimentoData } = useQuery(
    'estabelecimento-dashboard',
    async () => {
      try {
        const response = await api.get('/api/estabelecimentos/me/estabelecimento');
        return response.data.data?.estabelecimento;
      } catch (error) {
        console.error('Erro ao buscar estabelecimento:', error);
        return null;
      }
    }
  );

  const aceitaPedidos = estabelecimentoData?.configuracoes?.aceitaPedidos !== false;

  const toggleAceitaPedidosMutation = useMutation(
    async (novoValor) => {
      return api.patch(`/api/estabelecimentos/${estabelecimentoData?._id}/aceita-pedidos`, {
        aceitaPedidos: novoValor
      });
    },
    {
      onSuccess: (_, novoValor) => {
        toast.success(novoValor ? '✅ Pedidos online abertos' : '🚫 Pedidos online fechados');
        queryClient.invalidateQueries('estabelecimento-dashboard');
        queryClient.invalidateQueries('estabelecimento-padrao');
        queryClient.invalidateQueries('estabelecimento-caixa');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao atualizar status de pedidos');
      }
    }
  );

  const { data: dashboardData, isLoading } = useQuery(
    ['dashboard', estabelecimentoData?._id],
    async () => {
      if (!estabelecimentoData?._id) {
        return null;
      }
      const response = await api.get(`/api/faturamento/dashboard?estabelecimento=${estabelecimentoData._id}`);
      return response.data.data;
    },
    {
      enabled: !!estabelecimentoData?._id
    }
  );

  // Buscar contas a pagar de entregadores
  const { data: contasPagarEntregadores } = useQuery(
    'contas-pagar-entregadores',
    async () => {
      try {
        const estabelecimento = queryClient.getQueryData('estabelecimento')?.data?.estabelecimento?._id;
        if (!estabelecimento) {
          // Tentar buscar estabelecimento
          try {
            const estabResponse = await api.get('/api/estabelecimentos/me/estabelecimento');
            const estabId = estabResponse.data.data?.estabelecimento?._id;
            if (!estabId) return { contas: [] };
            const response = await api.get(`/api/financeiro/contas?estabelecimento=${estabId}&tipo=pagar&categoria=Entregadores&status=pendente`);
            return response.data.data;
          } catch (err) {
            return { contas: [] };
          }
        }
        const response = await api.get(`/api/financeiro/contas?estabelecimento=${estabelecimento}&tipo=pagar&categoria=Entregadores&status=pendente`);
        return response.data.data;
      } catch (error) {
        console.error('Erro ao buscar contas a pagar:', error);
        return { contas: [] };
      }
    },
    {
      refetchInterval: 30000
    }
  );

  const totalPagarEntregadores = contasPagarEntregadores?.contas?.reduce((acc, c) => 
    acc + (c.valor - (c.valorPago || 0)), 0) || 0;

  if (isLoading) {
    return <div className="loading">Carregando...</div>;
  }

  const stats = [
    {
      title: 'Faturamento Hoje',
      value: `R$ ${dashboardData?.hoje?.faturamento?.toFixed(2) || '0.00'}`,
      icon: DollarSign,
      premiumClass: 'premium-green'
    },
    {
      title: 'Vendas Hoje',
      value: dashboardData?.hoje?.transacoes ?? dashboardData?.hoje?.pedidos ?? 0,
      icon: ShoppingCart,
      premiumClass: 'premium-blue'
    },
    {
      title: 'Faturamento do Mês',
      value: `R$ ${dashboardData?.mesAtual?.faturamento?.toFixed(2) || '0.00'}`,
      icon: TrendingUp,
      premiumClass: 'premium-purple'
    },
    {
      title: 'Vendas do Mês',
      value: dashboardData?.mesAtual?.transacoes ?? dashboardData?.mesAtual?.pedidos ?? 0,
      icon: Clock,
      premiumClass: 'premium-orange'
    }
  ];

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <Logo size="small" />
        <div className="dashboard-title">
          <h2>Dashboard</h2>
          <p>Visão geral do seu negócio</p>
        </div>
        {estabelecimentoData?._id && (
          <button
            onClick={() => toggleAceitaPedidosMutation.mutate(!aceitaPedidos)}
            disabled={toggleAceitaPedidosMutation.isLoading}
            style={{
              marginLeft: 'auto',
              padding: '0.75rem 1rem',
              background: aceitaPedidos ? '#dc3545' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: toggleAceitaPedidosMutation.isLoading ? 'not-allowed' : 'pointer',
              fontWeight: 700,
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              opacity: toggleAceitaPedidosMutation.isLoading ? 0.7 : 1
            }}
            title={aceitaPedidos ? 'Fechar pedidos online (site/WhatsApp)' : 'Abrir pedidos online (site/WhatsApp)'}
          >
            <Power size={18} />
            {aceitaPedidos ? 'FECHAR PEDIDOS' : 'ABRIR PEDIDOS'}
          </button>
        )}
      </div>

      {estabelecimentoData?._id && (
        <div style={{
          background: aceitaPedidos ? '#d4edda' : '#f8d7da',
          border: `2px solid ${aceitaPedidos ? '#28a745' : '#dc3545'}`,
          borderRadius: '0.5rem',
          padding: '0.75rem 1rem',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: aceitaPedidos ? '#28a745' : '#dc3545'
            }} />
            <strong style={{ fontSize: '0.95rem' }}>
              {aceitaPedidos ? '✅ Pedidos online ABERTOS' : '🚫 Pedidos online FECHADOS'}
            </strong>
          </div>
          <small style={{ color: '#666' }}>
            {aceitaPedidos ? 'Site e WhatsApp podem receber pedidos' : 'Bloqueado para novos pedidos no site e WhatsApp'}
          </small>
        </div>
      )}

      <div className="stats-grid">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className={`stat-card ${stat.premiumClass}`}>
              <div className="stat-icon">
                <Icon size={28} />
              </div>
              <div className="stat-content">
                <p className="stat-title">{stat.title}</p>
                <p className="stat-value">{stat.value}</p>
              </div>
            </div>
          );
        })}
        
        {totalPagarEntregadores > 0 && (
          <div 
            className="stat-card premium-red" 
            onClick={() => navigate('/admin/financeiro?tab=contas-pagar&categoria=Entregadores')}
            style={{ cursor: 'pointer' }}
          >
            <div className="stat-icon">
              <Users size={28} />
            </div>
            <div className="stat-content">
              <p className="stat-title">Contas a Pagar (Entregadores)</p>
              <p className="stat-value">R$ {totalPagarEntregadores.toFixed(2)}</p>
              <p className="stat-subtitle" style={{ fontSize: '0.75rem', marginTop: '4px', opacity: 0.8 }}>
                {contasPagarEntregadores?.contas?.length || 0} pendente(s)
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="dashboard-section">
        <h3>Resumo Mensal</h3>
        <div className="summary-card">
          <div className="summary-item">
            <span>Faturamento Atual</span>
            <strong>R$ {dashboardData?.mesAtual?.faturamento?.toFixed(2) || '0.00'}</strong>
          </div>
          <div className="summary-item">
            <span>Mês Anterior</span>
            <strong>R$ {dashboardData?.mesAnterior?.faturamento?.toFixed(2) || '0.00'}</strong>
          </div>
          <div className="summary-item">
            <span>Variação</span>
            <strong className={dashboardData?.variacao >= 0 ? 'positive' : 'negative'}>
              {dashboardData?.variacao >= 0 ? '+' : ''}{dashboardData?.variacao?.toFixed(2) || '0.00'}%
            </strong>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

